import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useLocation, useNavigate, useNavigationType } from 'react-router-dom'
import { Button, Spin, Tooltip } from 'antd'
import { ReloadOutlined } from '@ant-design/icons'
import B2BLayout from '../../layouts/b2b'
import fetchAPI from '../../../lib/utils/fetch-api'
import { buildMcpUiUrl, getAi2UiBaseUrl } from '../../utils/mcp-ui'
import { useIsAdmin } from '../../hooks/useIsAdmin/useIsAdmin'
import { IframeControls, IframeElement, IframeLoader, IframeWrapper } from './styles'
import {
  createMcpEmbedTokenBroker,
  isUsableMcpEmbedToken,
  McpEmbedToken,
  McpEmbedTokenBroker,
} from './embed-token-broker'

const MCP_UI_ROUTE_PREFIX = '/ai'
const LEGACY_MCP_UI_ROUTE_PREFIX = '/mcp-ui'
const DEFAULT_MCP_UI_PATH = '/ai/agents'

type MCPUiEmbedProps = {
  routePrefix?: string
  legacyRoutePrefix?: string
  defaultPath?: string
  iframeBaseUrl?: string
  iframePathPrefix?: string
  apiBasePath?: string
  reportsEnumPath?: string
  embedTokenPath?: string
  pageTitle?: string
  dealerMappingEnabled?: boolean
  adaptiveHeight?: boolean
  viewportScrollPathPrefixes?: string[]
  fullBleed?: boolean
}

type ReportsEnumData = {
  roles: Array<{ id: number; name: string }>
  offices: Array<{ id: number; name: string }>
}

const mapLegacyPath = (pathname: string, routePrefix = MCP_UI_ROUTE_PREFIX, legacyRoutePrefix = LEGACY_MCP_UI_ROUTE_PREFIX) => {
  if (!legacyRoutePrefix || !pathname.startsWith(legacyRoutePrefix)) return pathname
  const suffix = pathname.slice(legacyRoutePrefix.length) || ''
  const mappedSuffix = suffix
    .replace(/^\/mcp-agents/, '/agents')
    .replace(/^\/mcp-reports/, '/reports')
  return `${routePrefix}${mappedSuffix || '/agents'}`
}

const resolveMcpUiPath = (
  pathname: string,
  routePrefix = MCP_UI_ROUTE_PREFIX,
  defaultPath = DEFAULT_MCP_UI_PATH,
  legacyRoutePrefix = LEGACY_MCP_UI_ROUTE_PREFIX
) => {
  if (!pathname) return defaultPath
  const resolved = mapLegacyPath(pathname, routePrefix, legacyRoutePrefix)
  if (resolved === routePrefix || resolved === `${routePrefix}/`) {
    return defaultPath
  }
  return resolved
}

const matchesPathPrefix = (pathname: string, prefix: string) => {
  const normalizedPrefix = prefix.replace(/\/+$/, '')
  if (!normalizedPrefix) return false
  return pathname === normalizedPrefix || pathname.startsWith(`${normalizedPrefix}/`)
}

export const MCPUiEmbed: React.FC<MCPUiEmbedProps> = ({
  routePrefix = MCP_UI_ROUTE_PREFIX,
  legacyRoutePrefix = LEGACY_MCP_UI_ROUTE_PREFIX,
  defaultPath = DEFAULT_MCP_UI_PATH,
  iframeBaseUrl,
  iframePathPrefix,
  apiBasePath = '',
  reportsEnumPath = '/api/mcp/reports/enum',
  embedTokenPath,
  pageTitle = 'AI Отчеты',
  dealerMappingEnabled = true,
  adaptiveHeight = false,
  viewportScrollPathPrefixes = [],
  fullBleed = false,
}) => {
  const location = useLocation()
  const navigate = useNavigate()
  const navigationType = useNavigationType()
  const isAdmin = useIsAdmin()
  const sessionData = useSelector((state: any) => state.session?.sessionData)
  const currentUserId = String(sessionData?.user?.id || '').trim()
  const [isLoading, setIsLoading] = useState(true)
  const [cacheBustToken, setCacheBustToken] = useState('')
  const [iframeHeight, setIframeHeight] = useState<number | null>(null)
  const skipNextSrcSyncRef = useRef(false)
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const iframeLoadedRef = useRef(false)
  const reportsEnumCacheRef = useRef<ReportsEnumData | null>(null)
  const reportsEnumPendingRef = useRef<Promise<ReportsEnumData | null> | null>(null)
  const embedTokenCacheRef = useRef<McpEmbedToken | null>(null)
  const embedTokenPendingRef = useRef<Promise<McpEmbedToken | null> | null>(null)
  const embedTokenBrokerRef = useRef<McpEmbedTokenBroker | null>(null)

  const resolvedPath = useMemo(
    () => resolveMcpUiPath(location.pathname, routePrefix, defaultPath, legacyRoutePrefix),
    [defaultPath, legacyRoutePrefix, location.pathname, routePrefix]
  )
  const usesViewportScroll = viewportScrollPathPrefixes.some(prefix => matchesPathPrefix(resolvedPath, prefix))
  const usesAdaptiveHeight = adaptiveHeight && !usesViewportScroll
  const iframePath = useMemo(() => {
    if (!iframePathPrefix) return resolvedPath
    const suffix = resolvedPath.startsWith(routePrefix)
      ? resolvedPath.slice(routePrefix.length)
      : resolvedPath
    return `${iframePathPrefix}${suffix || ''}`
  }, [iframePathPrefix, resolvedPath, routePrefix])

  const targetUrl = useMemo(() => {
    const baseUrl = buildMcpUiUrl(iframePath, iframeBaseUrl)
    const url = new URL(baseUrl, window.location.origin)
    url.search = location.search
    if (cacheBustToken) {
      url.searchParams.set('_cb', cacheBustToken)
    }
    url.hash = location.hash
    return url.toString()
  }, [iframePath, iframeBaseUrl, location.search, location.hash, cacheBustToken])

  const [iframeSrc, setIframeSrc] = useState(targetUrl)

  const targetOrigin = useMemo(() => {
    try {
      return new URL(targetUrl).origin
    } catch {
      return ''
    }
  }, [targetUrl])

  const wsBase = useMemo(() => {
    if (!apiBasePath) return ''
    const origin = window.location.origin
    const normalizedApiBasePath = apiBasePath.startsWith('/') ? apiBasePath : `/${apiBasePath}`
    const apiBaseUrl = new URL(normalizedApiBasePath, origin)
    apiBaseUrl.protocol = apiBaseUrl.protocol === 'https:' ? 'wss:' : 'ws:'
    apiBaseUrl.pathname = `${apiBaseUrl.pathname.replace(/\/+$/, '')}/ws`
    apiBaseUrl.search = ''
    apiBaseUrl.hash = ''
    return apiBaseUrl.toString()
  }, [apiBasePath])

  const loadReportsEnum = useCallback(async () => {
    if (reportsEnumCacheRef.current) return reportsEnumCacheRef.current
    if (reportsEnumPendingRef.current) return reportsEnumPendingRef.current

    reportsEnumPendingRef.current = fetchAPI(reportsEnumPath)
      .then((response) => response?.data || null)
      .catch(() => null)
      .finally(() => {
        reportsEnumPendingRef.current = null
      })

    const data = await reportsEnumPendingRef.current
    if (data) {
      reportsEnumCacheRef.current = data
    }
    return data
  }, [reportsEnumPath])

  useEffect(() => {
    const broker = createMcpEmbedTokenBroker({ userId: currentUserId })
    embedTokenBrokerRef.current = broker
    if (isUsableMcpEmbedToken(embedTokenCacheRef.current)) {
      broker?.publishToken(embedTokenCacheRef.current)
    }

    return () => {
      broker?.close()
      if (embedTokenBrokerRef.current === broker) {
        embedTokenBrokerRef.current = null
      }
    }
  }, [currentUserId])

  const loadEmbedToken = useCallback(async () => {
    if (!embedTokenPath) return null

    const cached = embedTokenCacheRef.current
    if (isUsableMcpEmbedToken(cached)) {
      return cached
    }
    if (embedTokenPendingRef.current) return embedTokenPendingRef.current

    embedTokenPendingRef.current = (async () => {
      const sharedToken = await embedTokenBrokerRef.current?.requestToken()
      if (isUsableMcpEmbedToken(sharedToken)) {
        return sharedToken
      }

      return fetchAPI(embedTokenPath).then(response => {
        const data = response?.data || null
        if (!data?.accessToken || !data?.expiresAt) return null
        const token = {
          accessToken: String(data.accessToken),
          expiresAt: String(data.expiresAt),
        }
        return isUsableMcpEmbedToken(token) ? token : null
      })
    })()
      .catch((error) => {
        console.error('[ai2] failed to create embed token', error)
        return null
      })
      .finally(() => {
        embedTokenPendingRef.current = null
      })

    const data = await embedTokenPendingRef.current
    if (isUsableMcpEmbedToken(data)) {
      embedTokenCacheRef.current = data
      embedTokenBrokerRef.current?.publishToken(data)
    }
    return data
  }, [embedTokenPath])

  const postHostConfig = useCallback((target: Window, origin: string) => {
    loadEmbedToken().then((token) => {
      if (!token?.accessToken) return
      target.postMessage({
        type: 'mcp-ui:host-config',
        payload: {
          accessControlEnabled: true,
          dealerMappingEnabled,
          apiBase: apiBasePath ? `${window.location.origin}${apiBasePath}` : window.location.origin,
          wsBase: wsBase || undefined,
          navigationBase: window.location.origin,
          accessToken: token.accessToken,
          theme: {
            bg: '#ffffff',
            surface: '#ffffff',
          },
        },
      }, origin || targetOrigin || '*')
    })
  }, [apiBasePath, dealerMappingEnabled, loadEmbedToken, targetOrigin, wsBase])

  useEffect(() => {
    const mappedLegacy = mapLegacyPath(location.pathname, routePrefix, legacyRoutePrefix)
    if (mappedLegacy !== location.pathname) {
      navigate(`${mappedLegacy}${location.search}${location.hash}`, { replace: true })
      return
    }

    const handleMessage = (event: MessageEvent) => {
      if (!event?.data || typeof event.data !== 'object') return
      if (targetOrigin && event.origin !== targetOrigin) return

      const { type, path, search, hash } = event.data as {
        type?: string
        path?: string
        search?: string
        hash?: string
        url?: string
        height?: number
      }

      if (type === 'mcp-ui:resize') {
        if (!usesAdaptiveHeight) return
        const height = Number((event.data as any).height)
        if (!Number.isFinite(height) || height <= 0) return
        setIframeHeight(Math.ceil(height))
        return
      }

      if (type === 'mcp-ui:request-host-config') {
        if (!event.source) return
        postHostConfig(event.source as Window, event.origin)
        return
      }

      if (type === 'mcp-ui:request-reports-enum') {
        loadReportsEnum().then((data) => {
          if (!data || !event.source) return
          const payload = { type: 'mcp-ui:reports-enum', payload: data }
          const target = event.source as Window
          target.postMessage(payload, event.origin || targetOrigin || '*')
        })
        return
      }

      if (type === 'mcp-ui:external-navigate') {
        const rawUrl = String((event.data as any).url || '').trim()
        try {
          const url = new URL(rawUrl)
          if (url.origin === 'https://oauth.yandex.ru') window.location.assign(url.toString())
        } catch {}
        return
      }

      if (type !== 'mcp-ui:navigate') return

      const rawNormalizedPath = path
        ? path.startsWith('/') ? path : `/${path}`
        : defaultPath
      const normalizedPath = iframePathPrefix && rawNormalizedPath.startsWith(iframePathPrefix)
        ? `${routePrefix}${rawNormalizedPath.slice(iframePathPrefix.length) || ''}`
        : rawNormalizedPath

      const searchPart = typeof search === 'string' ? search : ''
      const hashPart = typeof hash === 'string' ? hash : ''
      const nextPath = normalizedPath.startsWith(routePrefix)
        ? normalizedPath
        : `${routePrefix}${normalizedPath}`
      const nextUrl = `${nextPath}${searchPart}${hashPart}`

      const currentUrl = `${location.pathname}${location.search}${location.hash}`
      if (currentUrl === nextUrl) return

      const shouldReplace =
        location.pathname === routePrefix ||
        location.pathname === `${routePrefix}/`

      skipNextSrcSyncRef.current = true
      navigate(nextUrl, { replace: shouldReplace })
    }

    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [defaultPath, iframePathPrefix, legacyRoutePrefix, loadReportsEnum, location.hash, location.pathname, location.search, navigate, postHostConfig, routePrefix, targetOrigin, usesAdaptiveHeight])

  useEffect(() => {
    if (!usesAdaptiveHeight) {
      setIframeHeight(null)
    }
  }, [usesAdaptiveHeight])

  useEffect(() => {
    if (skipNextSrcSyncRef.current) {
      skipNextSrcSyncRef.current = false
      return
    }

    const canPost = iframeLoadedRef.current && iframeRef.current?.contentWindow
    if (canPost) {
      iframeRef.current?.contentWindow?.postMessage({
        type: 'mcp-ui:parent-navigate',
        path: iframePath,
        search: location.search,
        hash: location.hash,
        replace: navigationType !== 'PUSH'
      }, targetOrigin || '*')
      return
    }

    if (iframeSrc !== targetUrl) {
      iframeLoadedRef.current = false
      setIsLoading(true)
      setIframeHeight(null)
      setIframeSrc(targetUrl)
    }
  }, [targetUrl, iframeSrc, iframePath, location.search, location.hash, navigationType, targetOrigin])

  const handleForceIframeReload = useCallback(() => {
    iframeLoadedRef.current = false
    setIsLoading(true)
    setIframeHeight(null)
    setCacheBustToken(String(Date.now()))
  }, [])

  return (
    <B2BLayout
      breadcrumbs={[
        { title: 'Главная', href: '/' },
        { title: 'AI Отчеты' }
      ]}
      pageTitle={pageTitle}
    >
      <IframeWrapper
        $fullBleed={fullBleed}
        $height={usesAdaptiveHeight ? iframeHeight : null}
        $viewportScroll={usesViewportScroll}
      >
        {isAdmin && (
          <IframeControls>
            <Tooltip title="Обновить iframe" placement="left">
              <Button
                aria-label="Обновить iframe"
                shape="circle"
                icon={<ReloadOutlined />}
                onClick={handleForceIframeReload}
              />
            </Tooltip>
          </IframeControls>
        )}
        {isLoading && (
          <IframeLoader>
            <Spin />
          </IframeLoader>
        )}
        <IframeElement
          title="MCP UI"
          ref={iframeRef}
          src={iframeSrc}
          scrolling={usesAdaptiveHeight ? 'no' : undefined}
          onLoad={() => {
            iframeLoadedRef.current = true
            setIsLoading(false)
            if (iframeRef.current?.contentWindow) {
              postHostConfig(iframeRef.current.contentWindow, targetOrigin)
            }
          }}
        />
      </IframeWrapper>
    </B2BLayout>
  )
}

export const AIUiEmbed: React.FC = () => (
  <MCPUiEmbed
    routePrefix="/ai"
    legacyRoutePrefix=""
    defaultPath="/ai/chats"
    iframeBaseUrl={getAi2UiBaseUrl()}
    iframePathPrefix="/ai/embed"
    apiBasePath="/api/mcp2"
    reportsEnumPath="/api/mcp2/reports/enum"
    embedTokenPath="/api/mcp2/embed-token"
    pageTitle="AI"
    dealerMappingEnabled={false}
    adaptiveHeight
    viewportScrollPathPrefixes={['/ai/chats', '/ai/reports']}
  />
)

export const AI2UiEmbed: React.FC = () => (
  <MCPUiEmbed
    routePrefix="/ai2"
    legacyRoutePrefix=""
    defaultPath="/ai2/chats"
    iframeBaseUrl={getAi2UiBaseUrl()}
    iframePathPrefix="/ai/embed"
    apiBasePath="/api/mcp2"
    reportsEnumPath="/api/mcp2/reports/enum"
    embedTokenPath="/api/mcp2/embed-token"
    pageTitle="AI 2"
    dealerMappingEnabled={false}
    adaptiveHeight
    viewportScrollPathPrefixes={['/ai2/chats', '/ai2/reports']}
  />
)

export default MCPUiEmbed
